import { useState, useEffect } from 'react';
import api from '../api';

export default function FrameworksPage() {
  const [frameworks,       setFrameworks]       = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [openFramework,    setOpenFramework]     = useState(null); // framework id
  const [controls,         setControls]         = useState({});   // { [fwId]: [...] }
  const [loadingControls,  setLoadingControls]  = useState(null);
  const [checklist,        setChecklist]        = useState({});   // { [controlId]: [...] }
  const [openControl,      setOpenControl]      = useState(null); // control db id
  const [newItems,         setNewItems]         = useState({});   // { [controlId]: '' }
  const [addingItem,       setAddingItem]       = useState(null); // control db id
  const [editingItem,      setEditingItem]      = useState(null); // { id, label }

  useEffect(() => {
    api.get('/api/frameworks')
      .then(r => setFrameworks(r.data))
      .finally(() => setLoading(false));
  }, []);

  async function toggleFramework(fwId) {
    if (openFramework === fwId) {
      setOpenFramework(null);
      return;
    }
    setOpenFramework(fwId);
    if (!controls[fwId]) {
      setLoadingControls(fwId);
      try {
        const r = await api.get(`/api/frameworks/${fwId}/controls`);
        setControls(prev => ({ ...prev, [fwId]: r.data }));
      } finally {
        setLoadingControls(null);
      }
    }
  }

  async function toggleControl(controlDbId) {
    if (openControl === controlDbId) {
      setOpenControl(null);
      return;
    }
    setOpenControl(controlDbId);
    if (!checklist[controlDbId]) {
      try {
        const r = await api.get(`/api/controls/${controlDbId}/checklist`);
        setChecklist(prev => ({ ...prev, [controlDbId]: r.data }));
      } catch {
        setChecklist(prev => ({ ...prev, [controlDbId]: [] }));
      }
    }
  }

  async function handleAddItem(e, controlDbId) {
    e.preventDefault();
    const label = (newItems[controlDbId] || '').trim();
    if (!label) return;
    setAddingItem(controlDbId);
    try {
      const r = await api.post(`/api/controls/${controlDbId}/checklist`, { label });
      setChecklist(prev => ({
        ...prev,
        [controlDbId]: [...(prev[controlDbId] || []), r.data],
      }));
      setNewItems(prev => ({ ...prev, [controlDbId]: '' }));
    } finally {
      setAddingItem(null);
    }
  }

  async function handleDeleteItem(controlDbId, itemId) {
    if (!window.confirm('Item verwijderen?')) return;
    await api.delete(`/api/controls/item/${itemId}`);
    setChecklist(prev => ({
      ...prev,
      [controlDbId]: prev[controlDbId].filter(i => i.id !== itemId),
    }));
  }

  async function handleSaveEdit(e) {
    e.preventDefault();
    if (!editingItem || !editingItem.label.trim()) return;
    await api.put(`/api/controls/item/${editingItem.id}`, { label: editingItem.label.trim() });
    // Update in local state
    setChecklist(prev => {
      const updated = { ...prev };
      for (const cid of Object.keys(updated)) {
        updated[cid] = updated[cid].map(i =>
          i.id === editingItem.id ? { ...i, label: editingItem.label.trim() } : i
        );
      }
      return updated;
    });
    setEditingItem(null);
  }

  // Groepeer controls per sectie
  function groupBySections(ctrlList) {
    const map = {};
    for (const c of ctrlList) {
      const sec = c.section || '—';
      if (!map[sec]) map[sec] = [];
      map[sec].push(c);
    }
    return map;
  }

  return (
    <>
      <div className="page-header">
        <h2>Frameworks</h2>
        <span className="text-muted small">Beheer de bewijschecklist per control</span>
      </div>

      <div className="page-body">
        <div className="alert alert-info d-flex gap-2 py-2 mb-4">
          <i className="bi bi-info-circle mt-1 flex-shrink-0" />
          <div className="small">
            Voeg per control een lijst toe van <strong>te vragen documenten</strong>.
            Deze items verschijnen als geheugensteuntje op elke bevindingenpagina — voor alle audits met dit framework.
          </div>
        </div>

        {loading ? (
          <div className="text-muted"><span className="spinner-border spinner-border-sm me-2" />Laden…</div>
        ) : (
          <div className="d-flex flex-column gap-3">
            {frameworks.map(fw => {
              const isOpen   = openFramework === fw.id;
              const ctrlList = controls[fw.id] || [];
              const sections = isOpen ? groupBySections(ctrlList) : {};

              return (
                <div key={fw.id} className="card">
                  {/* Framework header */}
                  <div
                    className="card-header d-flex justify-content-between align-items-center"
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => toggleFramework(fw.id)}
                  >
                    <div>
                      <span className="fw-semibold">{fw.name}</span>
                      {fw.version && <span className="text-muted small ms-2">({fw.version})</span>}
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      {loadingControls === fw.id && (
                        <span className="spinner-border spinner-border-sm text-muted" />
                      )}
                      <i className={`bi bi-chevron-${isOpen ? 'up' : 'down'} text-muted`} />
                    </div>
                  </div>

                  {/* Controls per sectie */}
                  {isOpen && (
                    <div className="card-body p-0">
                      {ctrlList.length === 0 ? (
                        <div className="text-muted small p-3">Geen controls gevonden.</div>
                      ) : (
                        Object.entries(sections).map(([section, sectionControls]) => (
                          <div key={section}>
                            {/* Sectie-header */}
                            <div
                              className="px-3 py-2 fw-semibold small"
                              style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', borderTop: '1px solid #e2e8f0', color: '#475569' }}
                            >
                              {section}
                            </div>

                            {/* Controls */}
                            {sectionControls.map(ctrl => {
                              const isCtrlOpen  = openControl === ctrl.id;
                              const items       = checklist[ctrl.id] || [];
                              // Geladen? gebruik de live telling; anders de server-telling (meteen zichtbaar).
                              const itemCount   = checklist[ctrl.id] ? items.length : (ctrl.checklist_count ?? 0);

                              return (
                                <div key={ctrl.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                  {/* Control rij */}
                                  <div
                                    className="d-flex align-items-center gap-2 px-3 py-2"
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => toggleControl(ctrl.id)}
                                  >
                                    <span className="text-muted font-monospace small flex-shrink-0" style={{ minWidth: 52 }}>
                                      {ctrl.control_id}
                                    </span>
                                    <span className="small flex-grow-1">{ctrl.title}</span>
                                    {itemCount !== null && itemCount > 0 && (
                                      <span className="badge bg-primary rounded-pill" style={{ fontSize: 10 }}>
                                        {itemCount}
                                      </span>
                                    )}
                                    <i className={`bi bi-chevron-${isCtrlOpen ? 'up' : 'down'} text-muted`} style={{ fontSize: 11 }} />
                                  </div>

                                  {/* Checklist panel */}
                                  {isCtrlOpen && (
                                    <div className="px-3 pb-3 pt-1" style={{ background: '#fafbfc' }}>
                                      {/* Bestaande items */}
                                      {items.length > 0 ? (
                                        <ul className="list-unstyled mb-2">
                                          {items.map(item => (
                                            <li key={item.id} className="d-flex align-items-center gap-2 py-1"
                                              style={{ borderBottom: '1px solid #f1f5f9' }}>
                                              <i className="bi bi-file-earmark-text text-muted flex-shrink-0" style={{ fontSize: 13 }} />

                                              {editingItem?.id === item.id ? (
                                                <form onSubmit={handleSaveEdit} className="d-flex gap-2 flex-grow-1">
                                                  <input
                                                    autoFocus
                                                    className="form-control form-control-sm"
                                                    value={editingItem.label}
                                                    onChange={e => setEditingItem(prev => ({ ...prev, label: e.target.value }))}
                                                  />
                                                  <button type="submit" className="btn btn-sm btn-primary flex-shrink-0">
                                                    <i className="bi bi-check2" />
                                                  </button>
                                                  <button type="button" className="btn btn-sm btn-outline-secondary flex-shrink-0"
                                                    onClick={() => setEditingItem(null)}>
                                                    <i className="bi bi-x" />
                                                  </button>
                                                </form>
                                              ) : (
                                                <>
                                                  <span className="small flex-grow-1">{item.label}</span>
                                                  <button
                                                    type="button"
                                                    className="btn btn-sm p-0 text-muted"
                                                    style={{ lineHeight: 1 }}
                                                    title="Bewerken"
                                                    onClick={() => setEditingItem({ id: item.id, label: item.label })}
                                                  >
                                                    <i className="bi bi-pencil" style={{ fontSize: 12 }} />
                                                  </button>
                                                  <button
                                                    type="button"
                                                    className="btn btn-sm p-0 text-danger ms-1"
                                                    style={{ lineHeight: 1 }}
                                                    title="Verwijderen"
                                                    onClick={() => handleDeleteItem(ctrl.id, item.id)}
                                                  >
                                                    <i className="bi bi-trash" style={{ fontSize: 12 }} />
                                                  </button>
                                                </>
                                              )}
                                            </li>
                                          ))}
                                        </ul>
                                      ) : (
                                        <p className="text-muted small mb-2">Nog geen items voor deze control.</p>
                                      )}

                                      {/* Nieuw item toevoegen */}
                                      <form
                                        onSubmit={e => handleAddItem(e, ctrl.id)}
                                        className="d-flex gap-2"
                                      >
                                        <input
                                          type="text"
                                          className="form-control form-control-sm"
                                          placeholder="Naam van een te vragen bewijsstuk…"
                                          value={newItems[ctrl.id] || ''}
                                          onChange={e => setNewItems(prev => ({ ...prev, [ctrl.id]: e.target.value }))}
                                          disabled={addingItem === ctrl.id}
                                        />
                                        <button
                                          type="submit"
                                          className="btn btn-sm btn-outline-primary flex-shrink-0"
                                          disabled={addingItem === ctrl.id || !(newItems[ctrl.id] || '').trim()}
                                        >
                                          {addingItem === ctrl.id
                                            ? <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12 }} />
                                            : <><i className="bi bi-plus me-1" />Toevoegen</>}
                                        </button>
                                      </form>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
